import React, { useState, useEffect } from "react";
import { refreshToken } from "../utils/auth";
import "../styles/Profile.css";
import WalletWithdraw from "./WalletWithdraw";
import { SquarePen } from "lucide-react";

const ProfileCard = ({
  onTopUp,
  onClose,
  walletBalance,
  onWithdrawSuccess,
}) => {
  const [username, setUsername] = useState("");
  const [fplId, setFplId] = useState("");
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [loading, setLoading] = useState(false); // for disabling Save button during request

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const BACKEND_URL =
          process.env.REACT_APP_API_URL || "http://localhost:5000";
        let res = await fetch(`${BACKEND_URL}/api/user`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        });

        if (res.status === 401) {
          const newToken = await refreshToken();

          if (newToken) {
            res = await fetch(`${BACKEND_URL}/api/user`, {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${newToken}`,
              },
              credentials: "include",
            });
          }
        }

        const data = await res.json();
        if (data.username) setUsername(data.username);
        if (data.fplId) setFplId(data.fplId);
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    };
    fetchUserData();
  }, []);

  // Handle save username
  const handleSaveUsername = async () => {
    if (!newUsername.trim()) {
      alert("Username cannot be empty");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const BACKEND_URL =
        process.env.REACT_APP_API_URL || "http://localhost:5000";

      let res = await fetch(`${BACKEND_URL}/api/user/username`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({ newUsername }),
      });

      if (res.status === 401) {
        const newToken = await refreshToken();

        if (newToken) {
          res = await fetch(`${BACKEND_URL}/api/user/username`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${newToken}`,
            },
            credentials: "include",
            body: JSON.stringify({ newUsername }),
          });
        }
      }

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Failed to update username");
        return;
      }

      setUsername(data.username);
      setEditMode(false);
      alert("Username updated successfully!");
    } catch (err) {
      console.error("Error updating username:", err);
      alert("Something went wrong while updating username.");
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawSubmit = async ({
    mpesaNumber,
    confirmMpesaNumber,
    amount,
  }) => {
    const BACKEND_URL = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");

    try {
      let res = await fetch(`${BACKEND_URL}/api/withdraw`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({ mpesaNumber, confirmMpesaNumber, amount }),
      });

      if (res.status === 401) {
        const newToken = await refreshToken();

        if (newToken) {
          res = await fetch(`${BACKEND_URL}/api/withdraw`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${newToken}`,
            },
            credentials: "include",
            body: JSON.stringify({ mpesaNumber, confirmMpesaNumber, amount }),
          });
        }
      }

      const data = await res.json();

      if (!res.ok) {
        return {
          status: "failed",
          message: data.message || "Withdrawal failed",
        };
      }

      onWithdrawSuccess?.(data.wallet);

      return data;
    } catch (err) {
      console.error("Withdrawal request error:", err);
      alert("An error occurred while submitting withdrawal.");
    }
  };

  const handleLogout = async () => {
    if (window.confirm("Are you sure you want to log out?")) {
      try {
        const API_URL =
          process.env.REACT_APP_API_URL || "http://localhost:5000";

        await fetch(`${API_URL}/logout`, {
          method: "POST",
          credentials: "include",
        });

        localStorage.removeItem("username");
        localStorage.removeItem("email");
        localStorage.removeItem("fplId");
        localStorage.removeItem("token");
        localStorage.removeItem("isGuest");

        window.location.href = "/";
      } catch (err) {
        console.error("Logout error:", err);
        localStorage.clear();
        window.location.href = "/";
      }
    }
  };

  return (
    <div className="profile-card-overlay" onClick={onClose}>
      <div className="profile-card" onClick={(e) => e.stopPropagation()}>
        <div className="profile-header">
          <div className="profile-avatar">👤</div>
          {editMode ? (
            <div className="edit-username-section">
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                placeholder="Enter new username"
                className="username-input"
                maxLength={15}
              />
              <div className="edit-buttons">
                <button
                  onClick={handleSaveUsername}
                  className="save-button"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <h3>{username}</h3>
              <div className="edit-username-button">
                <button
                  onClick={() => {
                    setNewUsername(username);
                    setEditMode(true);
                  }}
                  className="edit-button"
                >
                  <SquarePen size={16} style={{ marginRight: "6px" }} />
                  Edit Username
                </button>
              </div>
            </>
          )}
          {fplId && <p className="fpl-id">FPL ID: {fplId}</p>}
        </div>

        <div className="wallet-info">
          <h4>Wallet Balance</h4>
          <p className="balance">KES {walletBalance}</p>
          <div className="TopUp-Profile">
            <button className="top-up-button" onClick={onTopUp}>
              Top Up Wallet
            </button>
          </div>
          <div className="withdraw-profile">
            <button
              className="withdraw-button"
              onClick={() => setShowWithdraw(true)}
            >
              Withdraw
            </button>
          </div>
          <div className="logout-profile">
            <button className="logout-button" onClick={handleLogout}>
              Log Out
            </button>
          </div>
        </div>
      </div>
      {showWithdraw && (
        <WalletWithdraw
          onClose={() => setShowWithdraw(false)}
          onSubmit={handleWithdrawSubmit}
          currentBalance={walletBalance}
        />
      )}
    </div>
  );
};

export default ProfileCard;
